/**
 * Whether a message counts as edited, for the "(Edited)" label.
 *
 * `updatedAt` does NOT equal `createdAt` on a freshly inserted row. The insert hook in
 * `packages/backend/src/db/hooks.ts` stamps them with two separate `new Date()` calls, so
 * a row inserted across a millisecond boundary lands with `updatedAt` 1 ms after
 * `createdAt`. `Message.updatedAt` is `TIMESTAMPTZ NOT NULL` with no DB default, so that
 * skew reaches the wire and an exact comparison paints "(Edited)" on messages nobody ever
 * touched.
 *
 * A tolerance is safe because nothing genuine can land inside it. The Edit affordance is
 * not clickable until the post-send refetch replaces the optimistic row (`isPending` ->
 * `pointer-events-none`), and `requireDirty` in `channel-message-edit.tsx` keeps Save
 * disabled until a real keystroke — so the fastest physically possible edit is ~1.5-2s
 * out, against a sub-millisecond skew. One second sits in the middle of that gap.
 *
 * An `updatedAt` at or before `createdAt` is never an edit: mirror rows copy the source
 * row's timestamps verbatim, pin/unpin/link/adopt preserve `updatedAt` via a
 * self-referential raw, and clocks drift backwards.
 */

import { differenceInMilliseconds, isValid, parseISO } from "date-fns";

/** Below this, a later `updatedAt` is DB insert skew rather than a human edit. */
const EDIT_SKEW_TOLERANCE_MS = 1_000;

export function isMessageEdited(message: {
  createdAt: string;
  updatedAt?: string | null;
}): boolean {
  if (!message.updatedAt) {
    return false;
  }
  const created = parseISO(message.createdAt);
  const updated = parseISO(message.updatedAt);
  if (!isValid(created) || !isValid(updated)) {
    return false;
  }
  return differenceInMilliseconds(updated, created) > EDIT_SKEW_TOLERANCE_MS;
}
